import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SearchIcon from "@mui/icons-material/Search";
import DataGridViewport from "../components/DataGridViewport";
import { useUserContext } from "../hooks/UserContext";
import { useProveedores } from "../hooks/ProveedoresContext";
import { ProductoCatalogoItem } from "../services/productoService";
import { CompraDetalleItem, CompraReporteItem, fetchGetDetalleCompra, fetchGetReporteCompras } from "../services/compraService";
import { isAbortError } from "../services/fetchService";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import { columnasDetalleCompra } from "../components/detalleColumnas";

const toDateInput = (date: Date) => date.toISOString().slice(0, 10);
const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
const money = (value: unknown) => CurrencyFormatter.format(Number(value ?? 0));
const dateText = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("es-AR");
};

const metricSx = {
  p: 2,
  height: "100%",
  borderLeft: "4px solid",
  borderColor: "primary.main",
};

export const reporteComprasColumns: GridColDef[] = [
  { field: "fecha", headerName: "Fecha", minWidth: 115, valueFormatter: (value) => dateText(value) },
  { field: "proveedor", headerName: "Proveedor", minWidth: 220, flex: 1 },
  { field: "tipo", headerName: "Tipo", minWidth: 135 },
  { field: "comprobante", headerName: "Comprobante", minWidth: 170, valueGetter: (_, row) => `${row.letraFactura} ${row.puntoVenta}-${row.numeroFactura}`.trim() },
  { field: "numeroRemito", headerName: "Remito", minWidth: 120 },
  { field: "subtotal", headerName: "Subtotal", minWidth: 125, align: "right", headerAlign: "right", valueFormatter: (value) => money(value) },
  { field: "iva", headerName: "IVA", minWidth: 115, align: "right", headerAlign: "right", valueGetter: (_, row) => Number(row.iva105 ?? 0) + Number(row.iva21 ?? 0) + Number(row.iva27 ?? 0), valueFormatter: (value) => money(value) },
  { field: "percepciones", headerName: "Perc./Imp.", minWidth: 130, align: "right", headerAlign: "right", valueGetter: (_, row) => Number(row.percepcionIva ?? 0) + Number(row.ingresosBrutos ?? 0) + Number(row.impuestosInternos ?? 0) + Number(row.tem ?? 0), valueFormatter: (value) => money(value) },
  { field: "bonificacion", headerName: "Bonif.", minWidth: 115, align: "right", headerAlign: "right", valueFormatter: (value) => money(value) },
  { field: "totalFactura", headerName: "Total", minWidth: 135, align: "right", headerAlign: "right", valueFormatter: (value) => money(value) },
  { field: "formaPago", headerName: "Forma pago", minWidth: 150 },
];

const ReporteCompras = () => {
  const { user } = useUserContext();
  const { proveedores, loading: loadingProveedores } = useProveedores();
  const [proveedor, setProveedor] = useState<ProductoCatalogoItem | null>(null);
  const [fechaDesde, setFechaDesde] = useState(toDateInput(firstDay));
  const [fechaHasta, setFechaHasta] = useState(toDateInput(today));
  const [fechaDesdeAplicada, setFechaDesdeAplicada] = useState(toDateInput(firstDay));
  const [fechaHastaAplicada, setFechaHastaAplicada] = useState(toDateInput(today));
  const [proveedorAplicado, setProveedorAplicado] = useState<ProductoCatalogoItem | null>(null);
  const [items, setItems] = useState<CompraReporteItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({
    totalSubtotal: 0,
    totalIva: 0,
    totalPercepciones: 0,
    totalBonificacion: 0,
    totalFactura: 0,
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detalleCompra, setDetalleCompra] = useState<CompraDetalleItem[]>([]);
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);

  const abrirDetalleCompra = async (compra: CompraReporteItem) => {
    if (!user) return;
    setDetalleAbierto(true);
    setCargandoDetalle(true);
    setErrorDetalle(null);
    try {
      setDetalleCompra(await fetchGetDetalleCompra(user, compra.idCompra, compra.idLocal));
    } catch {
      setDetalleCompra([]);
      setErrorDetalle("No se pudo cargar el detalle de la compra.");
    } finally {
      setCargandoDetalle(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setItems([]);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchGetReporteCompras(user, paginationModel.page, paginationModel.pageSize, {
      fechaDesde: fechaDesdeAplicada,
      fechaHasta: fechaHastaAplicada,
      idProveedor: proveedorAplicado?.id,
    }, controller.signal)
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
        setTotals({
          totalSubtotal: data.totalSubtotal,
          totalIva: data.totalIva,
          totalPercepciones: data.totalPercepciones,
          totalBonificacion: data.totalBonificacion,
          totalFactura: data.totalFactura,
        });
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.log(err);
        setItems([]);
        setTotal(0);
        setError("No se pudo cargar el reporte de compras.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [fechaDesdeAplicada, fechaHastaAplicada, paginationModel.page, paginationModel.pageSize, proveedorAplicado?.id, user]);

  const aplicarFiltros = () => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFechaDesdeAplicada(fechaDesde);
    setFechaHastaAplicada(fechaHasta);
    setProveedorAplicado(proveedor);
  };

  const rows = useMemo(() => items.map((item) => ({ ...item, id: item.idCompra })), [items]);

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ReceiptLongIcon color="primary" />
        <Typography variant="h4">Reporte de compras</Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={proveedores}
              value={proveedor}
              loading={loadingProveedores}
              getOptionLabel={(option) => option.nombre}
              onChange={(_, value) => setProveedor(value)}
              renderInput={(params) => <TextField {...params} label="Proveedor" />}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" label="Desde" value={fechaDesde} onChange={(event) => setFechaDesde(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" label="Hasta" value={fechaHasta} onChange={(event) => setFechaHasta(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <SearchIcon />} onClick={aplicarFiltros} disabled={loading} fullWidth>
              Generar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">Subtotal</Typography><Typography variant="h6">{money(totals.totalSubtotal)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">IVA</Typography><Typography variant="h6">{money(totals.totalIva)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">Perc./Imp.</Typography><Typography variant="h6">{money(totals.totalPercepciones)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">Bonificacion</Typography><Typography variant="h6">{money(totals.totalBonificacion)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={{ ...metricSx, borderColor: "success.main" }}><Typography color="text.secondary">Total</Typography><Typography variant="h6">{money(totals.totalFactura)}</Typography></Paper></Grid>
      </Grid>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGridViewport height={650} minWidth={1500}>
          <DataGrid
            rows={rows}
            columns={reporteComprasColumns}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => abrirDetalleCompra(params.row)}
          />
        </DataGridViewport>
      )}
      <Dialog open={detalleAbierto} onClose={() => setDetalleAbierto(false)} fullWidth maxWidth="lg">
        <DialogTitle>Detalle de compra</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            La forma y el costo corresponden a los datos guardados en la compra. La cantidad base representa las unidades ingresadas al stock.
          </Typography>
          {cargandoDetalle ? <CircularProgress /> : errorDetalle ? <Alert severity="error">{errorDetalle}</Alert> : (
            <DataGrid autoHeight rows={detalleCompra.map((item, index) => ({ ...item, id: index }))} columns={columnasDetalleCompra(money)} pageSizeOptions={[10, 20]} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReporteCompras;
